/**
 * Borrower / owner name normalization — frontend mirror of
 * backend/utils/name_normalizer.py.
 *
 * Used by CreateOrderForm to render a live preview ("Will be saved as: …")
 * so users see exactly what the backend will store. Backend remains the
 * single source of truth — frontend is purely cosmetic.
 *
 * Keep these rules in sync with name_normalizer.py.
 */

const UPPER_TOKENS = new Set([
  'LLC', 'INC', 'INC.', 'LLP', 'LP', 'LTD', 'LTD.', 'PLLC', 'PC',
  'PA', 'CO', 'CORP', 'CORP.',
  'USA', 'DBA', 'ETUX', 'ETAL',
  'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'TR',
  'JR', 'JR.', 'SR', 'SR.',
]);

const LOWER_JOINERS = new Set(['and', 'or', 'of', 'the', '&']);

const NAME_PARTICLES = new Set([
  'von', 'van', 'der', 'den', 'de', 'del', 'della', 'di', 'da',
  'le', 'la', 'el', 'bin', 'ibn', 'al',
]);

function capitalizeToken(token) {
  if (!token) return token;
  if (token.indexOf('-') !== -1) {
    return token.split('-').map(capitalizeToken).join('-');
  }
  const upper = token.toUpperCase();
  const upperClean = upper.replace(/[.,;]+$/, '');
  if (UPPER_TOKENS.has(upperClean) || UPPER_TOKENS.has(upper)) {
    return upper;
  }
  const lower = token.toLowerCase();
  if (LOWER_JOINERS.has(lower) || NAME_PARTICLES.has(lower)) {
    return lower;
  }
  let titled = lower.charAt(0).toUpperCase() + lower.slice(1);
  // Mc / Mac / O' / D' prefixes
  if (/^Mc[a-z]/.test(titled)) titled = 'Mc' + titled.charAt(2).toUpperCase() + titled.slice(3);
  else if (/^Mac[a-z]{2,}/.test(titled)) titled = 'Mac' + titled.charAt(3).toUpperCase() + titled.slice(4);
  else if (/^O'[a-z]/.test(titled)) titled = "O'" + titled.charAt(2).toUpperCase() + titled.slice(3);
  else if (/^D'[a-z]/.test(titled)) titled = "D'" + titled.charAt(2).toUpperCase() + titled.slice(3);
  return titled;
}

export function normalizeBorrowerName(raw) {
  if (raw === null || raw === undefined) return raw;
  let text = String(raw).trim();
  if (!text) return '';
  text = text.replace(/\s*-\s*/g, '-');
  text = text.replace(/\s+/g, ' ');
  const tokens = text.split(' ').map(capitalizeToken);
  if (tokens.length > 0) {
    const first = tokens[0].toLowerCase();
    if (LOWER_JOINERS.has(first) || NAME_PARTICLES.has(first)) {
      tokens[0] = tokens[0].charAt(0).toUpperCase() + tokens[0].slice(1);
    }
    if (tokens.length > 1) {
      const last = tokens[tokens.length - 1].toLowerCase();
      if (LOWER_JOINERS.has(last) || NAME_PARTICLES.has(last)) {
        tokens[tokens.length - 1] = tokens[tokens.length - 1].charAt(0).toUpperCase() + tokens[tokens.length - 1].slice(1);
      }
    }
  }
  return tokens.join(' ');
}

export default normalizeBorrowerName;
